/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */

import {
	forwardRef,
	type ForwardRefExoticComponent,
	type RefAttributes,
	useCallback,
	useImperativeHandle,
	useState,
} from 'react';

import { cssMap as cssMapCompiled, keyframes } from '@compiled/react';

import { cssMap, jsx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';

import { type IconContainerRef } from './IconContainerRef';

const styles = cssMap({
	iconContainerStyles: {
		paddingTop: token('space.050'),
		paddingRight: token('space.050'),
		paddingBottom: token('space.050'),
		paddingLeft: token('space.050'),
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		borderRadius: token('radius.full'),
	},
});

const spinGradientBurst = keyframes({
	'0%': { transform: 'rotate(0deg)' },
	'50%': { transform: 'rotate(180deg)' },
	'100%': { transform: 'rotate(360deg)' },
});

/* eslint-disable  @atlaskit/design-system/ensure-design-token-usage, @atlaskit/ui-styling-standard/no-nested-selectors */
const compiledStyles = cssMapCompiled({
	defaultStyle: {
		color: token('color.icon.subtle'),
		backgroundColor: token('elevation.shadow.overflow.perimeter'),
		borderRadius: token('radius.full'),
	},

	bgGradientAngularContent: {
		position: 'relative',
		zIndex: 1,
		backgroundColor: 'black', // To hide the gradient behind content
		borderRadius: token('radius.full'),
		scale: '.90',
	},

	bgGradientAngularAnimated: {
		position: 'relative',
		background: 'transparent',
		color: 'white',
		'&::before': {
			content: '""',
			position: 'absolute',
			top: 0,
			left: 0,
			right: 0,
			bottom: 0,
			background: `conic-gradient(
			from 0deg,
			#1868db 0deg,
			#1868db 30deg,
			#fca700 31deg,
			#fca700 40deg,
			#fca700 75deg,
			#fca700 85deg,
			#bf63f3 86deg,
			#bf63f3 120deg,
			#bf63f3 165deg,
			#bf63f3 175deg,
			#82b536 176deg,
			#82b536 210deg,
			#82b536 255deg,
			#82b536 265deg,
			#1868db 266deg,
			#1868db 300deg,
			#1868db 360deg
		  )`,
			borderRadius: token('radius.full'),
			animation: `${spinGradientBurst} 0.4s ease-out 0.1s`,
			zIndex: 0,
		},

		'& > *': {
			position: 'relative',
			zIndex: 1,
		},
	},

	bgGradientAngularStatic: {
		color: 'white',
		background: `conic-gradient(
			from 0deg,
			#1868db 0deg,
			#1868db 45deg,
			#fca700 46deg,
			#fca700 90deg,
			#fca700 135deg,
			#bf63f3 136deg,
			#bf63f3 180deg,
			#bf63f3 225deg,
			#82b536 226deg,
			#82b536 270deg,
			#82b536 315deg,
			#1868db 316deg,
			#1868db 360deg
		  )`,
		borderRadius: token('radius.full'),
	},
});
/* eslint-enable  @atlaskit/design-system/ensure-design-token-usage, @atlaskit/ui-styling-standard/no-nested-selectors */

type IconContainerProps = {
	children: React.ReactNode;
	selected: boolean;
};
export const IconContainer: ForwardRefExoticComponent<
	IconContainerProps & RefAttributes<IconContainerRef>
> = forwardRef<IconContainerRef, IconContainerProps>(({ children, selected }, ref) => {
	const [runninAnimation, setRunningAnimation] = useState(false);
	const runAnimation = useCallback(() => {
		setRunningAnimation(true);
		setTimeout(() => {
			setRunningAnimation(false);
		}, 400);
	}, []);

	useImperativeHandle(ref, () => ({
		runAnimation,
	}));

	if (!selected) {
		return (
			<div css={compiledStyles.defaultStyle}>
				<div css={styles.iconContainerStyles}>{children}</div>
			</div>
		);
	}

	const content = (
		<div css={compiledStyles.bgGradientAngularContent}>
			<div css={styles.iconContainerStyles}>{children}</div>
		</div>
	);
	return runninAnimation ? (
		<div css={compiledStyles.bgGradientAngularAnimated}>{content}</div>
	) : (
		<div css={compiledStyles.bgGradientAngularStatic}>{content}</div>
	);
});
